/**
 * Invitation Service
 *
 * API integration for survey invitation management.
 * Provides methods to create, retrieve, and delete survey invitations.
 *
 * Note: These methods delegate to surveyService which has the actual implementations.
 * This separate file provides a focused API for invitation-related operations.
 */

import { surveyService } from './surveyService';
import type { SurveyInvitation } from '@/types';
import type { CreateInvitationDto } from '@/types/invitation';

/**
 * Invitation service - focused API for managing survey invitations
 */
export const invitationService = {
  /**
   * Get all invitations for a survey
   *
   * @param surveyId - The survey ID
   * @returns Array of survey invitations
   */
  async getInvitations(surveyId: number): Promise<SurveyInvitation[]> {
    return surveyService.getInvitations(surveyId);
  },

  /**
   * Create a new invitation for a survey
   *
   * @param surveyId - The survey ID
   * @param data - Invitation creation data (email, maxUses, expiresDate are all optional)
   * @returns The created invitation with generated token
   */
  async createInvitation(
    surveyId: number,
    data: CreateInvitationDto
  ): Promise<SurveyInvitation> {
    return surveyService.createInvitation(surveyId, data);
  },

  /**
   * Delete an invitation
   *
   * @param invitationId - The invitation ID to delete
   */
  async deleteInvitation(invitationId: number): Promise<void> {
    return surveyService.deleteInvitation(invitationId);
  },

  /**
   * Send invitation email (if email was provided when creating invitation)
   *
   * @param invitationId - The invitation ID
   */
  async sendInvitationEmail(invitationId: number): Promise<void> {
    return surveyService.sendInvitationEmail(invitationId);
  },
};

export default invitationService;
